import React, { Component } from "react";
import PropTypes from "prop-types";
import hoistStatics from "hoist-non-react-statics";

const FormContext = React.createContext();
export const withForm = (MainComponent, definedProps = {}) => {
  const C = allProps => {
    const { wrappedComponentRef, ...remainingProps } = allProps;
    return (
      <FormContext.Consumer>
        {dataProps => {
          return (
            <MainComponent
              {...remainingProps}
              {...dataProps}
              {...definedProps}
              ref={wrappedComponentRef}
            />
          );
        }}
      </FormContext.Consumer>
    );
  };
  C.displayName = `withForm(${MainComponent.displayName ||
    MainComponent.name})`;
  C.WrappedComponent = MainComponent;
  return hoistStatics(C, MainComponent);
};

class FormProvider extends Component {
  constructor(props) {
    super(props);
    const self = this;
    self.state = {
      readonly: false,
      frozen: false,
      errors: []
    };
    self.form = {
      get: key => self.state[`form:${key}`],
      set: (key, value) => {
        if (self.state.readonly || self.state.frozen) {
          return;
        }
        const param = {};
        param[`form:${key}`] = value;
        self.setState(param);
      },
      getLabel: key => self.state[`label:${key}`],
      getError: key => self.state[`error:${key}`],
      setLabel: (key, value) => {
        if (self.state.readonly || self.state.frozen) {
          return;
        }
        const param = {};
        param[`label:${key}`] = value;
        self.setState(param);
      },
      setError: (key, value) => {
        if (self.state.readonly || self.state.frozen) {
          return;
        }
        const param = {};
        param[`error:${key}`] = value;
        self.setState(param);
      },
      showErrors: () => self.state.showErrors || false,
      isReadonly: () => self.state.readonly || false,
      isFrozen: () => self.state.frozen || false
    };
  }
  componentDidUpdate(prevProps, prevState, snapshot) {
    if (super.componentDidUpdate) {
      super.componentDidUpdate(prevProps, prevState, snapshot);
    }
    // readonly とfrozenの値が異なっている場合、調整する
    //   readonly設定とfrozen設定は同時には行わず、readonly設定のみ行う。
    //   readonly設定変更によるcomponent付け替え時にclearValされないようにするため。
    const self = this;
    if (self.state.readonly !== self.state.frozen) {
      self.setState({ frozen: self.state.readonly });
    }
    // if (self.state.readonly) {
    //   self.validate().catch(() => {
    //     backFromReadonly();
    //   });
    // }
  }
  // backFromReadonly () {
  //   // override
  // }
  getFormData() {
    const self = this;
    return Object.keys(self.state)
      .filter(key => /^form:/.test(key))
      .reduce((d, key) => {
        const value = self.state[key];
        if (value != null) {
          d[key.replace("form:", "")] = value;
        }
        return d;
      }, {});
  }
  setFormData(data) {
    if (!data) {
      return;
    }
    const self = this;
    const state = Object.keys(data).reduce((map, key) => {
      map[`form:${key}`] = data[key];
      return map;
    }, {});
    self.setState(state);
  }
  getErrors() {
    const self = this;
    let errors = Object.keys(self.state)
      .filter(key => /^error:/.test(key))
      .map(key => {
        const message = self.state[key];
        const name = key.replace("error:", "");
        return message ? { name, message } : false;
      })
      .filter(v => v);
    if (self.sortByName) {
      errors = self.sortByName(errors);
    }
    return errors;
  }
  validate() {
    const self = this;
    const data = self.getFormData();
    return new Promise((resolve, reject) => {
      const errors = self.getErrors();
      if (errors.length >= 1) {
        self.setState({ errors, showErrors: true, readonly: false });
        reject(errors);
        return;
      }
      self.setState({ errors: [], showErrors: false });
      resolve(data);
    });
  }
  render() {
    const self = this;
    return (
      <FormContext.Provider value={self.form}>
        {self.renderForm()}
      </FormContext.Provider>
    );
  }
}

export class FormBlock extends Component {
  static propTypes = {
    get: PropTypes.func.isRequired,
    set: PropTypes.func.isRequired,
    getLabel: PropTypes.func.isRequired,
    setLabel: PropTypes.func.isRequired,
    getError: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    isReadonly: PropTypes.func.isRequired,
    isFrozen: PropTypes.func.isRequired,
    showErrors: PropTypes.func.isRequired,
    required: PropTypes.bool
  };
  static extendPropTypes(types) {
    return Object.assign({}, FormBlock.propTypes, types);
  }
  constructor(props) {
    super(props);
    const self = this;
    self.getExtendedProps = self.getExtendedProps.bind(self);
    self.get = self.get.bind(self);
    self.set = self.set.bind(self);
    self.getError = self.getError.bind(self);
    self.setError = self.setError.bind(self);
    self.getLabel = self.getLabel.bind(self);
    self.setLabel = self.setLabel.bind(self);
    self.isReadonly = self.isReadonly.bind(self);
    self.isFrozen = self.isFrozen.bind(self);
    self.renderReadonly = self.renderReadonly.bind(self);
    self.renderInput = self.renderInput.bind(self);
  }
  getExtendedProps() {
    const self = this;
    const {
      get,
      set,
      getLabel,
      setLabel,
      getError,
      setError,
      isReadonly,
      isFrozen,
      showErrors,
      ...extended
    } = self.props;
    return extended;
  }
  get(key) {
    return this.props.get(key);
  }
  set(key, val) {
    this.props.set(key, val);
  }
  getLabel(key) {
    return this.props.getLabel(key);
  }
  setLabel(key, val) {
    this.props.setLabel(key, val);
  }
  getError(key) {
    return this.props.getError(key);
  }
  setError(key, val) {
    this.props.setError(key, val);
  }
  isReadonly() {
    return this.props.isReadonly();
  }
  isFrozen() {
    return this.props.isFrozen();
  }
  isRequired() {
    const self = this;
    if (self.props.required != null) {
      return self.props.required;
    }
    return Boolean(self.required);
  }
  renderReadonly() {
    throw new Error("method `renderReadonly` must be implemented");
  }
  renderInput() {
    throw new Error("method `renderInput` must be implemented");
  }
  render() {
    const self = this;
    if (self.isReadonly && self.isReadonly()) {
      return self.renderReadonly();
    }
    return self.renderInput();
  }
}

const ValidationMessages = {
  required: label => `${label}を入力してください。`
};
const ValidateFunctions = {
  zenkakuKana: function() {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      if (!/^[ァ-ヶー　（）]*$/.test(val)) {
        reject(`${label}は全角カタカナで入力してください。`);
        return;
      }
      resolve();
    });
  },
  tel: function() {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      if (!/^\d+-\d+-\d+$/.test(val)) {
        reject(`${label}の形式（電話番号）を正しく入力してください。`);
        return;
      }
      resolve();
    });
  },
  date: function() {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      if (!/^\d{4}\/\d{2}\/\d{2}$/.test(val)) {
        reject(`${label}の形式（日付）を正しく入力してください。`);
        return;
      }
      resolve();
    });
  },
  email: function() {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      // if (!/^[^@]+@[^@]+$/.test(val)) {
      if (
        !/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
          val
        )
      ) {
        reject(`${label}の形式（メールアドレス）を正しく入力してください。`);
        return;
      }
      resolve();
    });
  },
  postalcode: function() {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      if (!/^\d{3}-\d{4}$/.test(val)) {
        reject(`${label}の形式（郵便番号）を正しく入力してください。`);
        return;
      }
      resolve();
    });
  },
  watertap: function() {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      if (!/^\d{7}-\d{4}$/.test(val)) {
        reject(`${label}の形式（お客様番号）を正しく入力してください。`);
        return;
      }
      resolve();
    });
  },
  reservationCode: function() {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      if (!/^\d{4}-\d{6}$/.test(val)) {
        reject(`${label}の形式（予約番号）を正しく入力してください。`);
        return;
      }
      resolve();
    });
  },
  match: function(values) {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      if (!values || !values.some(v => v === val)) {
        reject(`${label}を正しく選択してください。`);
      }
      resolve();
    });
  },
  matchType: function() {
    const item = this;
    if (typeof item.getTypes !== "function" || !item.getTypes()) {
      return Promise.resolve();
    }
    const val = item.getVal();
    const label = item.getItemLabel();
    const values = Object.keys(item.getTypes());
    return new Promise((resolve, reject) => {
      if (!values || !values.some(v => String(v) === String(val))) {
        reject(`${label}を正しく選択してください。`);
      }
      resolve();
    });
  },
  matchWith: function(propName) {
    const item = this;
    const val = item.getVal();
    const expectedVal = item.get(propName);
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      if (val !== expectedVal) {
        reject(
          `${label}には${item.getLabel(propName)}と同じ値を入力してください。`
        );
      }
      resolve();
    });
  },
  maxLength: function() {
    const item = this;
    if (typeof item.getMaxLength !== "function" || !item.getMaxLength()) {
      return Promise.resolve();
    }
    const val = item.getVal();
    const label = item.getItemLabel();
    const maxLength = item.getMaxLength();
    return new Promise((resolve, reject) => {
      if (val.length > maxLength) {
        reject(`${label}は${maxLength}文字以内で入力してください。`);
        return;
      }
      resolve();
    });
  },
  sample: function() {
    const item = this;
    const val = item.getVal();
    const label = item.getItemLabel();
    return new Promise((resolve, reject) => {
      reject(`sample validation: ${label}, ${val}`);
    });
  }
};

export class FormItemValidation {
  constructor(formItem) {
    this.item = formItem;
  }
  run() {
    const self = this;
    const item = self.item;
    const label = item.getItemLabel();
    if (item.isEmpty()) {
      if (item.isRequired()) {
        return Promise.reject(ValidationMessages.required(label));
      }
      return Promise.resolve(true);
    }
    if (!item.validations || item.validations.length < 1) {
      return Promise.resolve(true);
    }
    return Promise.all(
      item.validations.map(validation => {
        return self.validate(validation);
      })
    ).then(() => true);
  }
  validate(validation, ...params) {
    const self = this;
    const item = self.item;
    if (typeof validation === "function") {
      return validation.apply(item, params);
    }
    if (typeof validation === "string") {
      if (/^self:/.test(validation)) {
        const method = item[validation.replace("self:", "")];
        if (typeof method === "function") {
          return method.apply(item, params);
        }
      }
      return self.validate(...[ValidateFunctions[validation]].concat(params));
    }
    if (Array.isArray(validation)) {
      return self.validate(...validation);
    }
    throw new Error("Called unrecognized validation.");
  }
}

export class FormItem extends FormBlock {
  static propTypes = FormBlock.extendPropTypes({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool
  });
  validations = [];
  static extendPropTypes(types) {
    types.name = PropTypes.string.isRequired;
    types.label = PropTypes.string.isRequired;
    types.required = PropTypes.bool;
    return FormBlock.extendPropTypes(types);
  }
  constructor(props, params = {}) {
    super(props);
    const self = this;
    self.state = self.state || {};
    self.state.error = false;
    self.validations = self.validations
      .concat(params.validations || [], props.validations || [])
      .filter((v, i, arr) => arr.indexOf(v) === i);
    self.setItemLabel(props.label);
  }
  defineValidations(validations = []) {
    const self = this;
    self.validations = self.validations
      .concat(validations || [])
      .filter((v, i, arr) => arr.indexOf(v) === i);
  }
  getValidations() {
    const self = this;
    return self.validations
      .concat(self.props.validations || [])
      .filter((v, i, arr) => arr.indexOf(v) === i);
  }
  componentDidUpdate() {
    const self = this;
    self.runValidation();
  }
  componentWillUnmount() {
    const self = this;
    // 選択肢などで、入力対象外となった項目の値とエラーを自動で削除する
    //   画面切り替えなどで一時的に除外されるときにも削除されてしまうため注意。
    //   そのような場合の削除を防ぐために、FormProviderのfrozen stateを利用する
    if (self.getVal() != null) {
      self.setVal(null);
    }
    if (self.getValError() != null) {
      self.setValError(null);
    }
  }
  getFormControlClassName() {
    return "form-control form-control-lg px-2";
  }
  getExtendedProps() {
    const { name, ...extended } = super.getExtendedProps();
    return extended;
  }
  runValidation() {
    const self = this;
    new FormItemValidation(self)
      .run()
      .then(() => {
        if (self.getValError()) {
          self.clearValError();
        }
      })
      .catch(errorMessage => {
        if (errorMessage !== self.getValError()) {
          self.setValError(errorMessage);
        }
      });
  }
  isEmpty() {
    const self = this;
    const val = self.getVal();
    return val == null || val === "";
  }
  getVal() {
    const self = this;
    return self.get(self.props.name);
  }
  setVal(v) {
    const self = this;
    return self.set(self.props.name, v);
  }
  setItemLabel(v) {
    const self = this;
    return self.setLabel(self.props.name, v);
  }
  getItemLabel() {
    const self = this;
    return (
      self.getLabel(self.props.name) || self.props.label || self.props.name
    );
  }
  hasValError() {
    const self = this;
    return Boolean(self.getValError());
  }
  getValError() {
    const self = this;
    return self.getError(self.props.name);
  }
  setValError(v) {
    const self = this;
    if (self.getValError() === v) {
      return;
    }
    self.setError(self.props.name, v);
    return;
  }
  clearValError() {
    const self = this;
    return self.setValError(null);
  }
  renderValError() {
    const self = this;
    const error = self.getValError();
    if (!error) {
      return null;
    }
    const showErrors = self.props.showErrors();
    const requiredError =
      ValidationMessages.required(self.getItemLabel()) === error;
    if (!showErrors && requiredError) {
      return null;
    }
    return (
      <div
        className={`alert alert-${
          showErrors ? "danger" : "info"
        } d-inline-block py-0 px-1 my-1 small`}
      >
        <strong class="text-danger">{error}</strong>
      </div>
    );
  }
}

export default FormProvider;
